/**
 * Create and initialise Tako popovers
 */

/* -----------------------------------------------------------------------------
 * Tako Popover
 * -------------------------------------------------------------------------- */

import { getIsRtl } from './utils/getIsRtl';
import { createHtmxPopover } from './htmx-components/htmx-tako-popover';
import { getLogger } from './utils/logger-context';
import { resolveComponentScope } from './utils/resolveComponentScope';

const COMPONENT_NAME = 'Popover';

const popoverInstances = new Map();

export const init = (unresolvedScope) => {
    const logger = getLogger();
    const scope = resolveComponentScope(unresolvedScope);

    function merge(originalObject, overrides) {
        return Object.values(
            []
                .concat(originalObject, overrides)
                .reduce(
                    (properties, currentProperty) => (
                        (properties[currentProperty.name] = Object.assign(
                            properties[currentProperty.name] || {},
                            currentProperty,
                        )),
                        properties
                    ),
                    {},
                ),
        );
    }

    const defaultLoggerArgs = {
        componentName: COMPONENT_NAME,
        scope,
    };
    logger.debug(`Initialising ${COMPONENT_NAME} component.`, { scope });

    const allHtmxPopoverButtons = scope.querySelectorAll(
        '[data-popover-hx-url]',
    );

    if (allHtmxPopoverButtons) {
        allHtmxPopoverButtons.forEach((htmxPopoverButton) => {
            createHtmxPopover(htmxPopoverButton);
        });
    }

    const allTakoPopovers = scope.querySelectorAll('.tako-popover');

    allTakoPopovers.forEach((takoPopover) => {
        try {
            const instanceLoggerArgs = {
                ...defaultLoggerArgs,
                element: takoPopover,
            };
            const takoPopoverId = takoPopover.getAttribute('id');

            if (!takoPopoverId) {
                logger.warn(
                    'Tako popovers must have a unique id - add one using the `id` attribute',
                    instanceLoggerArgs,
                );
                return;
            }

            const popoverButton = getPopoverButton(takoPopover);

            if (!popoverButton || !takoPopover) {
                logger.warn(
                    'Tako popovers must have a trigger element with `data-popover-target="popoverId"` and a div with the `tako-popover` class. Omitting component...',
                    instanceLoggerArgs,
                );
                return;
            }

            const popoverTitle =
                popoverButton.getAttribute('data-popover-title');

            if (popoverTitle) {
                addPopoverHeader(takoPopover, popoverTitle);
            }

            const popoverBody = takoPopover.querySelector(
                '.tako-popover__body',
            );

            if (!popoverBody) {
                addPopoverBodyWrapper(takoPopover);
            }

            addPopoverArrow(takoPopover);
            setAriaAttrs(takoPopover);

            // eslint-disable-next-line no-undef
            const popperPopoverInstance = Popper.createPopper(
                popoverButton,
                takoPopover,
                {
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: [0, 10],
                            },
                        },
                    ],
                },
            );

            takoPopover.addEventListener('htmx:afterSettle', () => {
                // This forces Popper to recalculate boundaries and position
                // based on the newly injected HTMX content dimensions.
                popperPopoverInstance.update();
            });

            function setPopperPopoverOptions(newModifier) {
                popperPopoverInstance.setOptions((options) =>
                    Object.assign(options, {
                        modifiers: merge(options.modifiers, newModifier),
                    }),
                );
            }

            let placement = takoPopover.getAttribute('data-placement');
            const placementOptions = [
                'top',
                'right',
                'end',
                'bottom',
                'left',
                'start',
            ];
            if (!placementOptions.some((option) => option === placement)) {
                placement = placementOptions[0];
            } else if (placement === 'end') {
                placement = getIsRtl() ? 'left' : 'right';
            } else if (placement === 'start') {
                placement = getIsRtl() ? 'right' : 'left';
            }

            popperPopoverInstance.setOptions({ placement });

            const open = () => {
                takoPopover.setAttribute('data-open', true);

                setPopperPopoverOptions([
                    { name: 'eventListeners', enabled: true },
                ]);

                popperPopoverInstance.update();

                popoverButton.setAttribute('aria-expanded', true);
                takoPopover.setAttribute('aria-hidden', false);

                // Emit a custom event when the popover is opened
                const openEvent = new CustomEvent('takoPopoverOpen', {
                    detail: {
                        takoPopover,
                        popoverButton,
                        id: takoPopover.id,
                    },
                });
                takoPopover.dispatchEvent(openEvent);
            };

            const close = () => {
                takoPopover.removeAttribute('data-open');

                setPopperPopoverOptions([
                    { name: 'eventListeners', enabled: false },
                ]);

                popoverButton.setAttribute('aria-expanded', false);
                takoPopover.setAttribute('aria-hidden', true);

                // Emit a custom event when the popover is closed
                const closeEvent = new CustomEvent('takoPopoverClose', {
                    detail: {
                        takoPopover,
                        popoverButton,
                        id: takoPopover.id,
                    },
                });
                takoPopover.dispatchEvent(closeEvent);
            };

            const handleTriggerClick = () => {
                if (takoPopover.hasAttribute('data-open')) {
                    close();
                } else {
                    open();
                }
            };

            const handleBodyClick = (event) => {
                if (
                    takoPopover.hasAttribute('data-open') &&
                    !takoPopover.contains(event.target) &&
                    !popoverButton.contains(event.target)
                ) {
                    close();
                }
            };

            popoverButton.addEventListener('click', handleTriggerClick);

            const dataFixedAttr = takoPopover.getAttribute('data-fixed');

            const isPopoverFixed =
                dataFixedAttr === 'true' || dataFixedAttr === '';

            if (!isPopoverFixed) {
                document.body.addEventListener('click', handleBodyClick);
            }

            /**
             * if popover button is part of a modal
             * - add an attribute to the popover to change its z-index
             * - move the popover outside the modal
             */
            if (popoverButton.closest('.tako-modal__dialog')) {
                takoPopover.setAttribute('data-modal-content', true);

                const popoverModalParent = takoPopover.closest('.tako-modal');

                if (popoverModalParent) {
                    popoverModalParent.insertAdjacentElement(
                        'afterend',
                        takoPopover,
                    );
                }
            }

            // Store the instance for external control via `toggle`
            popoverInstances.set(takoPopover, { open, close });
        } catch (error) {
            logger.error(error, {
                ...defaultLoggerArgs,
                element: takoPopover,
            });
        }
    });
};

const getPopoverButton = (popover) => {
    const popoverId = popover.getAttribute('id');
    const popoverButton = document.querySelector(
        `[data-popover-target='${popoverId}'],
        [data-popover-target='#${popoverId}']`,
    );

    return popoverButton;
};

const addPopoverHeader = (popover, title) => {
    const popoverHeader = document.createElement('h6');
    popoverHeader.innerText = title;

    popover.prepend(popoverHeader);
};

const addPopoverBodyWrapper = (popover) => {
    const popoverBody = document.createElement('div');
    popoverBody.classList.add('tako-popover__body');
    if (popover.childNodes.length > 0) {
        popoverBody.prepend(...popover.childNodes);
    }
    popover.appendChild(popoverBody);
};

const addPopoverArrow = (popover) => {
    const popoverArrow = document.createElement('div');
    popoverArrow.setAttribute('data-popper-arrow', true);
    popoverArrow.classList.add('tako-popover__arrow');
    popover.appendChild(popoverArrow);
};

const setAriaAttrs = (popover) => {
    const popoverId = popover.getAttribute('id');
    const popoverButton = getPopoverButton(popover);

    popover.setAttribute('aria-hidden', true);
    popoverButton.setAttribute('aria-expanded', false);
    popoverButton.setAttribute('aria-controls', popoverId);
};

/**
 * Tako Popover Functions
 */

const debounceDelay = 100;
const debounceTimeouts = new Map();

/**
 * Toggles the visibility of one or more Tako Popover components.
 *
 * @param {string} selector - Document query selector (id or class) to target the popover(s).
 *                            Accepts an id (e.g., '#my-popover') or a class (e.g., '.my-popover-class').
 * @param {boolean|null} [open=null] - Optional. If `true`, explicitly open the popover(s).
 *                                     If `false`, explicitly close the popover(s).
 *                                     If `null` (default), the popover's current state will be toggled.
 *
 * @example
 * // Toggle based on the current state (open if closed, close if open):
 * togglePopover('.my-popover-class');
 *
 * @example
 * // Explicitly open the popover(s):
 * togglePopover('#my-popover-id', true);
 *
 * @example
 * // Explicitly close the popover(s):
 * togglePopover('.my-popover-class', false);
 */
export const toggle = (selector, open = null) => {
    const logger = getLogger();

    // Support id's without the # prefix
    if (selector[0] !== '#' && selector[0] !== '.') {
        selector = `#${selector}`;
    }

    // Get all elements that match the selector (could be multiple if class selector is used)
    const takoPopoverElements = document.querySelectorAll(selector);

    if (!takoPopoverElements.length) {
        logger.warn(
            `No Tako Popover components found with the selector "${selector}".`,
        );
        return;
    }

    takoPopoverElements.forEach((takoPopoverElement) => {
        const instance = popoverInstances.get(takoPopoverElement);

        if (!instance) {
            logger.warn(
                `No instance found for the element with the selector "${selector}". Make sure the Tako Popover has been initialized.`,
            );
            return;
        }

        // Clear any existing timeout for this popover
        if (debounceTimeouts.has(takoPopoverElement)) {
            clearTimeout(debounceTimeouts.get(takoPopoverElement));
        }

        const shouldOpen =
            open !== null
                ? open
                : !takoPopoverElement.hasAttribute('data-open'); // If `open` is null, toggle based on current state

        // Handle debounce delay to ensure proper timing
        debounceTimeouts.set(
            takoPopoverElement,
            setTimeout(() => {
                if (shouldOpen) {
                    instance.open();
                } else {
                    instance.close();
                }

                // Remove the timeout once it's executed
                debounceTimeouts.delete(takoPopoverElement);
            }, debounceDelay),
        );
    });
};
